import { CircleSlash } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "~/components/ui";
import { cn } from "~/lib/utils";

interface AreaGeoItem {
  geoItemId: string;
  date?: string | null;
  endDate?: string | null;
  deactivatedAt?: string | null;
}

type AreaGeoItemPeriod = Pick<AreaGeoItem, "date" | "endDate">;

interface AreaGeoItemRow {
  geoItemId: string;
  deactivatedAt?: string | null;
  periods: AreaGeoItemPeriod[];
}

// A Record can link one GeoItem several times (one row per link entry), but the detail is addressed by
// GeoItem and lists every entry, so the list shows one row per GeoItem with each entry's period.
// deactivatedAt belongs to the (Record, GeoItem) pair, so every entry of a GeoItem carries the same value.
const toAreaGeoItemRows = (recordGeoItems: AreaGeoItem[]): AreaGeoItemRow[] =>
  recordGeoItems.reduce<AreaGeoItemRow[]>((rows, { geoItemId, date, endDate, deactivatedAt }) => {
    const period = { date, endDate };
    return rows.some((row) => row.geoItemId === geoItemId)
      ? rows.map((row) => (row.geoItemId === geoItemId ? { ...row, periods: [...row.periods, period] } : row))
      : [...rows, { geoItemId, deactivatedAt, periods: [period] }];
  }, []);

interface AreaGeoItemsListProps {
  recordGeoItems: AreaGeoItem[];
  selectedGeoItemId: string | null;
  onSelect: (geoItemId: string) => void;
}

export function AreaGeoItemsList({ recordGeoItems, selectedGeoItemId, onSelect }: AreaGeoItemsListProps) {
  const { t } = useTranslation();
  const [excludeDeactivated, setExcludeDeactivated] = useState(true);

  const items = useMemo(
    () => toAreaGeoItemRows(recordGeoItems.filter((rgi) => !rgi.deactivatedAt || !excludeDeactivated)),
    [recordGeoItems, excludeDeactivated],
  );

  return (
    <div className="flex flex-col gap-3 text-[13px]">
      <label htmlFor="area-exclude-deactivated" className="flex items-center gap-2 text-fg-1">
        <Checkbox
          id="area-exclude-deactivated"
          checked={excludeDeactivated}
          onCheckedChange={(checked) => setExcludeDeactivated(checked === true)}
        />
        <span>{t("feature.common.excludeDeactivated")}</span>
      </label>

      {items.length === 0 ? (
        <p className="text-fg-3">{t("feature.common.noData")}</p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {items.map((rgi) => {
            const active = rgi.geoItemId === selectedGeoItemId;
            return (
              <li key={rgi.geoItemId}>
                <button
                  type="button"
                  onClick={() => onSelect(rgi.geoItemId)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-[var(--radius-xs)] px-2 py-1.5 text-left",
                    active ? "bg-bg-tint text-primary-700" : "text-fg-1 hover:bg-bg-subtle",
                  )}
                >
                  {rgi.deactivatedAt && <CircleSlash className="h-3.5 w-3.5 shrink-0 text-warning" />}
                  <span className="flex flex-col tabular-nums">
                    {rgi.periods.map((period, index) => (
                      <span key={index}>
                        {period.date || "—"} ~ {period.endDate || "—"}
                      </span>
                    ))}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
